import { forwardRef, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Heart, Calendar, Stethoscope, Users, UserCircle, CalendarDays, BarChart3, Landmark, UserRoundPen, MessageCircle, HelpCircle, Settings, Mail, CheckCircle2, Check, BadgeCheck, CreditCard, Settings2, Pill, ShieldCheck, UserCog } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import HeartLoader from '../HeartLoader';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { tryingnavigate, setPendingNavigation, clearPendingNavigation } from '../../redux/blockingSlice';
import { getPatientById } from '../../redux/patientSlice';
import { getDoctorById } from '../../redux/doctorSlice';
import { getAdminById } from '../../redux/adminSlice';
import { isFeatureEnabled, FEATURE_KEYS } from '../../redux/featureSlice';
import { canUse } from '../../redux/subscriptionSlice';
import { getDoctorSetupStatus } from '../../utils/doctorSetupStatus';
import logo from '../../assets/img/logo-TT2.png';
import getFileURL from '../../utils/fileURL';


const Sidebar = forwardRef(({ isOpen, onClose }, ref) => {
  const { i18n, t } = useTranslation('sidebar');
  const { selectedPatient } = useSelector((state) => state.patient);
  const { selectedDoctor, isLoading } = useSelector((state) => state.doctor);
  const { admin } = useSelector((state) => state.admin);
  const { resnavigate, pendingNavigation } = useSelector((state) => state.blocking);
  // Admin-controlled feature flags. These gate the role-specific sub-items
  // below (Patient Library for doctors, Doctor Library for patients).
  // Patient Library is now gated by the doctor's subscription package AND the
  // admin global flag (canUse combines both). Falls back to hidden when the
  // doctor's plan doesn't grant 'patient_library' (e.g. the Free tier).
  const doctorPatientLibraryEnabled = useSelector((state) => canUse(state, 'patient_library'));
  // Doctor–patient chat is gated by the doctor's subscription plan.
  const doctorChatEnabled = useSelector((state) => canUse(state, 'doctor_patient_chat'));
  const onlinePaymentEnabled = useSelector((state) => canUse(state, 'online_payment'));
  const patientDoctorLibraryEnabled = useSelector((state) => isFeatureEnabled(state, FEATURE_KEYS.patientDoctorLibrary));
  // Super-admin-controlled: hide the Medicines nav (and medicines features) when off.
  const medicinesEnabled = useSelector((state) => isFeatureEnabled(state, FEATURE_KEYS.medicines));
  const location = useLocation();
  const userRole = localStorage.getItem('Role') || 'patient';
  const doctorId = localStorage.getItem('ID');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleProfileNavigation = (tabKey) => {
    const state = { tab: tabKey };
    const path = userRole === 'doctor'
      ? '/doctorProfile'
      : userRole === 'patient'
        ? '/patientProfile'
        : userRole === 'admin'
          ? '/adminProfile'
          : null;
    if (!path) return;
    if (resnavigate) {
      // Defer the navigation until the dirty tab's discard handler clears the block.
      dispatch(setPendingNavigation({ kind: 'route', value: path, state }));
      dispatch(tryingnavigate(true));
      return;
    }
    navigate(path, { state });
    if (window.innerWidth <= 1024 && typeof onClose === 'function') {
      onClose();
    }
  };

  // Dropdown states
  const [dashboardDropdownOpen, setDashboardDropdownOpen] = useState(false);
  const [appointmentDropdownOpen, setAppointmentDropdownOpen] = useState(false);
  const [reportsDropdownOpen, setReportsDropdownOpen] = useState(false);
  const [masterDropdownOpen, setMasterDropdownOpen] = useState(false);
  const [patientDropdownOpen, setPatientDropdownOpen] = useState(false);
  const [doctorDropdownOpen, setDoctorDropdownOpen] = useState(false);
  const [subscriptionDropdownOpen, setSubscriptionDropdownOpen] = useState(false);
  const [auditDropdownOpen, setAuditDropdownOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Reset image error
  useEffect(() => {
    setImageError(false);
  }, [selectedDoctor, selectedPatient, admin]);

  // Auto-open correct dropdown based on current path
  useEffect(() => {
    const path = location.pathname;

    if (userRole === 'admin') {
      const isDashboardActive = ['/adminDashboard', '/doctorDashboard'].includes(path);
      setDashboardDropdownOpen(isDashboardActive);

      const isAppointmentActive = ['/appointment', '/appointmentList'].includes(path);
      setAppointmentDropdownOpen(isAppointmentActive);

      const isReportsActive = ['/doctors', '/patients', '/count', '/doctor-status'].includes(path);
      setReportsDropdownOpen(isReportsActive);

      const isMasterActive = ['/speciality-category', '/speciality', '/master-support', '/translation', '/country', '/profession'].includes(path);
      setMasterDropdownOpen(isMasterActive);

      const isSubscriptionActive = ['/sub-items', '/subscription', '/subscriptionPayments', '/contactRequests'].includes(path);
      setSubscriptionDropdownOpen(isSubscriptionActive);
      const isAuditActive = ['/audit/api', '/audit/socket', '/audit/login', '/audit/activity', '/audit/appointments', '/audit/security', '/audit/ai'].includes(path);
      setAuditDropdownOpen(isAuditActive);
    } else if (userRole === 'doctor') {
      const isAppointmentActive = ['/appointment', '/appointmentList'].includes(path);
      setAppointmentDropdownOpen(isAppointmentActive);

      const isPatientActive = ['/myPatient', '/patient', '/patientProfile', '/patientLibrary'].includes(path);
      setPatientDropdownOpen(isPatientActive);
    } else if (userRole === 'patient') {
      const isAppointmentActive = ['/appointment', '/appointmentList'].includes(path);
      setAppointmentDropdownOpen(isAppointmentActive);

      const isDoctorActive = ['/myDoctor', '/doctor', '/doctorProfile', '/doctorLibrary'].includes(path);
      setDoctorDropdownOpen(isDoctorActive);
    } else {
      const isAppointmentActive = ['/appointment', '/appointmentList'].includes(path);
      setAppointmentDropdownOpen(isAppointmentActive);
    }
  }, [location.pathname, userRole]);

  // Load user profile
  useEffect(() => {
    if (!selectedPatient && userRole === 'patient' && doctorId) {
      dispatch(getPatientById(doctorId));
    } else if (!selectedDoctor && userRole === 'doctor' && doctorId) {
      dispatch(getDoctorById(doctorId));
    } else if (!admin && userRole === 'admin' && doctorId) {
      dispatch(getAdminById(doctorId));
    }
  }, [userRole, doctorId, dispatch, selectedPatient, selectedDoctor, admin]);

  // User info
  const userName =
    userRole === 'doctor'
      ? selectedDoctor?.name || t('sidebar.doctors')
      : userRole === 'patient'
        ? selectedPatient?.name || t('sidebar.patients')
        : userRole === 'admin'
          ? admin?.name || t('sidebar.admins')
          : t('sidebar.guest');

  const user = {
    name: userName,
    image:
      userRole === 'doctor' ? selectedDoctor?.image || ''
        : userRole === 'admin' ? admin?.image || ''
          : userRole === 'patient' ? selectedPatient?.image || ''
            : '',
  };

  // Calculate profile completion percentage
  const calculateProfileCompletion = () => {
    if (userRole === 'patient' && selectedPatient) {
      const fields = [
        'name', 'surname', 'gender', 'dob', 'email', 'phone',
        'address', 'country', 'city', 'zipCode', 'occupation', 'image'
      ];
      const filledFields = fields.filter(field => {
        const value = selectedPatient[field];
        return value !== null && value !== undefined && value !== '' &&
          (typeof value === 'string' ? value.trim() !== '' : true);
      });
      return Math.round((filledFields.length / fields.length) * 100);
    } else if (userRole === 'doctor' && selectedDoctor) {
      const fields = [
        'name', 'surname', 'gender', 'dob', 'email', 'phone',
        'licenseNumber', 'description', 'image'
      ];
      const filledFields = fields.filter(field => {
        const value = selectedDoctor[field];
        return value !== null && value !== undefined && value !== '' &&
          (typeof value === 'string' ? value.trim() !== '' : true);
      });

      // Check websiteLink array - must have at least 1 item
      const hasWebsiteLink = selectedDoctor.websiteLink &&
        Array.isArray(selectedDoctor.websiteLink) &&
        selectedDoctor.websiteLink.length >= 1;

      // Check certification array - must have at least 1 item
      const hasCertification = selectedDoctor.certification &&
        Array.isArray(selectedDoctor.certification) &&
        selectedDoctor.certification.length >= 1;

      // Total fields = regular fields (9) + websiteLink (1) + certification (1) = 11
      const totalFields = fields.length + 2;
      const completedFields = filledFields.length + (hasWebsiteLink ? 1 : 0) + (hasCertification ? 1 : 0);

      return Math.round((completedFields / totalFields) * 100);
    } else if (userRole === 'admin' && admin) {
      const fields = ['name', 'email', 'image'];
      const filledFields = fields.filter(field => {
        const value = admin[field];
        return value !== null && value !== undefined && value !== '' &&
          (typeof value === 'string' ? value.trim() !== '' : true);
      });
      return Math.round((filledFields.length / fields.length) * 100);
    }
    return 0;
  };

  const profileCompletion = calculateProfileCompletion();

  // Get progress bar color based on completion percentage
  const getProgressColor = (percentage) => {
    if (percentage < 34) {
      // Red for low completion (0-33%)
      return 'bg-gradient-to-r from-red-500 to-red-600';
    } else if (percentage < 67) {
      // Yellow/Orange for medium completion (34-66%)
      return 'bg-gradient-to-r from-yellow-500 to-orange-500';
    } else {
      // Green for high completion (67-100%)
      return 'bg-gradient-to-r from-emerald-400 to-emerald-600';
    }
  };

  // Get percentage text color based on completion
  const getPercentageTextColor = (percentage) => {
    if (percentage < 34) {
      return 'text-red-600';
    } else if (percentage < 67) {
      return 'text-orange-500';
    } else {
      return 'text-green-600';
    }
  };

  // Get doctor setup status using utility function
  const setupStatus = getDoctorSetupStatus(userRole, selectedDoctor);

  const isProfile100 = profileCompletion === 100;
  const isSetup100 = setupStatus ? (setupStatus.point1 && setupStatus.point2 && setupStatus.point3 && setupStatus.point4 && setupStatus.point5) : (userRole !== 'doctor');
  const showBadge = userRole === 'doctor' ? (isSetup100) : (isProfile100 && isSetup100);


  // super_admin sees everything an admin does PLUS the "Admins" management page,
  // Subscription → Sub-Items, and the whole Audit & Security section. A plain
  // admin has those three hidden. (Role is normalised to 'admin' for both; this
  // flag is the only client-side distinction.)
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';

  // Admin Menu Items
  const adminItems = [
    {
      name: t('sidebar.dashboard'),
      icon: LayoutDashboard,
      path: '/adminDashboard',
      id: 'nav-dashboard',
      hasDropdown: true,
      subItems: [
        { name: t('sidebar.my_dashboard'), path: '/adminDashboard', id: 'nav-admin-dashboard' },
        { name: t('sidebar.doctor_dashboard'), path: '/doctorDashboard', id: 'nav-doctor-dashboard' },
      ]
    },
    { name: t('sidebar.ai_reports', 'AI Reports'), icon: BarChart3, path: '/aiReports', id: 'nav-ai-reports' },
    {
      name: t('sidebar.appointment'),
      icon: Calendar,
      path: '/appointment',
      id: 'nav-appointment',
      hasDropdown: true,
      subItems: [
        { name: t('sidebar.calendar'), path: '/appointment', id: 'nav-appointment-calendar' },
        { name: t('sidebar.list'), path: '/appointmentList', id: 'nav-appointment-list' }
      ]
    },
    { name: t('sidebar.doctor'), icon: Stethoscope, path: '/doctor', id: 'nav-doctor', activePaths: ['/doctor', '/doctorProfile'] },
    { name: t('sidebar.patient'), icon: Users, path: '/patient', id: 'nav-patient', activePaths: ['/patient', '/patientProfile'] },
    // Admins management — super_admin only.
    ...(isSuperAdmin
      ? [{ name: t('sidebar.admins', 'Admins'), icon: UserCog, path: '/admins', id: 'nav-admins', activePaths: ['/admins', '/manageAdminProfile'] }]
      : []),
    { name: t('sidebar.myprofile'), icon: UserRoundPen, path: '/adminProfile', id: 'nav-admin-profile' },

    // Reports Section - Now Properly Working
    {
      name: t('sidebar.reports'),
      icon: BarChart3,
      path: '/doctors', // default route
      id: 'nav-reports',
      hasDropdown: true,
      subItems: [
        { name: t('sidebar.reports_doctors'), path: '/doctors', id: 'nav-reports-doctors' },
        { name: t('sidebar.reports_patients'), path: '/patients', id: 'nav-reports-patients' },
        { name: t('sidebar.reports_count'), path: '/count', id: 'nav-reports-count' },
        { name: t('sidebar.reports_doctor_status'), path: '/doctor-status', id: 'nav-reports-doctor-status' },
      ]
    },

    // Master Section
    {
      name: t('sidebar.master'),
      icon: Settings,
      path: '/speciality-category', // default route
      id: 'nav-master',
      hasDropdown: true,
      subItems: [
        { name: t('sidebar.speciality_category', 'Speciality Category'), path: '/speciality-category', id: 'nav-master-speciality-category' },
        { name: t('sidebar.speciality'), path: '/speciality', id: 'nav-master-speciality' },
        { name: t('sidebar.support'), path: '/master-support', id: 'nav-master-support' },
        // Translation — super_admin only (hidden from plain admins).
        ...(isSuperAdmin ? [{ name: t('sidebar.translation'), path: '/translation', id: 'nav-master-translation' }] : []),
        { name: t('sidebar.country', 'Country'), path: '/country', id: 'nav-master-country' },
        { name: t('sidebar.profession', 'Profession'), path: '/profession', id: 'nav-master-profession' },
      ]
    },


    // { name: t('sidebar.chat'), icon: MessageCircle, path: '/chat', id: 'nav-chat' },
    {
      name: t('sidebar.subscription', 'Subscription'),
      icon: CreditCard,
      path: '/subscription', // default route (first sub-item)
      id: 'nav-subscription',
      hasDropdown: true,
      subItems: [
        { name: t('sidebar.packages', 'Packages'), path: '/subscription', id: 'nav-subscription-packages' },
        // Sub-Items — super_admin only.
        ...(isSuperAdmin ? [{ name: t('sidebar.sub_items', 'Sub-Items'), path: '/sub-items', id: 'nav-subscription-sub-items' }] : []),
        { name: t('sidebar.payments', 'Payments'), path: '/subscriptionPayments', id: 'nav-subscription-payments' },
        { name: t('sidebar.contact_requests', 'Contact requests'), path: '/contactRequests', id: 'nav-contact-requests' },
      ]
    },
    // Audit & Security — super_admin only (hidden entirely for plain admins).
    ...(isSuperAdmin ? [{
      name: t('sidebar.audit_security', 'Audit & Security'),
      icon: ShieldCheck,
      path: '/audit/api', // default route (first sub-item)
      id: 'nav-audit',
      hasDropdown: true,
      subItems: [
        { name: t('sidebar.api_logs', 'API Logs'), path: '/audit/api', id: 'nav-audit-api' },
        { name: t('sidebar.socket_logs', 'Socket Logs'), path: '/audit/socket', id: 'nav-audit-socket' },
        { name: t('sidebar.login_logs', 'Login Logs'), path: '/audit/login', id: 'nav-audit-login' },
        { name: t('sidebar.activity_logs', 'Activity Logs'), path: '/audit/activity', id: 'nav-audit-activity' },
        { name: t('sidebar.appointment_logs', 'Appointment Logs'), path: '/audit/appointments', id: 'nav-audit-appointments' },
        { name: t('sidebar.security_monitoring', 'Security Monitoring'), path: '/audit/security', id: 'nav-audit-security' },
        { name: t('sidebar.ai_log', 'AI Log'), path: '/audit/ai', id: 'nav-audit-ai' },
      ]
    }] : []),
    { name: t('sidebar.send_invite'), icon: Mail, path: '/clicktogo', id: 'nav-clicktogo' },
    { name: t('sidebar.support'), icon: HelpCircle, path: '/support', id: 'nav-support' },
    { name: t('sidebar.settings', 'Settings'), icon: Settings2, path: '/settings', id: 'nav-settings' },
  ];

  const doctorItems = [
    { name: t('sidebar.dashboard'), icon: LayoutDashboard, path: '/doctorDashboard', id: 'nav-dashboard' },
    { name: t('sidebar.ai_reports', 'AI Reports'), icon: BarChart3, path: '/aiReports', id: 'nav-ai-reports' },
    {
      name: t('sidebar.appointment'),
      icon: Calendar,
      path: '/appointment',
      id: 'nav-appointment',
      hasDropdown: true,
      subItems: [
        { name: t('sidebar.calendar'), path: '/appointment', id: 'nav-appointment-calendar' },
        { name: t('sidebar.list'), path: '/appointmentList', id: 'nav-appointment-list' }
      ]
    },
    {
      name: t('sidebar.patient'),
      icon: Users,
      path: '/myPatient',
      id: 'nav-patient',
      hasDropdown: true,
      subItems: [
        { name: t('sidebar.my_patient'), path: '/myPatient', id: 'nav-my-patient', activePaths: ['/myPatient', '/patientProfile'] },
        // Gated by admin Settings → Doctor → Patient Library toggle.
        ...(doctorPatientLibraryEnabled ? [{ name: t('sidebar.patient_library'), path: '/patientLibrary', id: 'nav-patient-library' }] : []),
      ]
    },
    { name: t('sidebar.myprofile'), icon: UserRoundPen, path: '/doctorProfile', id: 'nav-doctor-profile' },
    // Medicines visibility is a super-admin-controlled feature flag (Settings → Doctor).
    ...(medicinesEnabled ? [{ name: t('sidebar.medicines', 'Medicines'), icon: Pill, path: '/medicines', id: 'nav-medicines' }] : []),
    { name: t('sidebar.doctor_settings', 'Settings'), icon: Settings2, path: '/doctorSettings', id: 'nav-doctor-settings' },
    // Gated by the doctor's plan (online_payment).
    ...(onlinePaymentEnabled ? [{ name: t('sidebar.payment'), icon: Landmark, path: '/payment', id: 'nav-payment' }] : []),
    { name: t('sidebar.my_subscription', 'My Subscription'), icon: CreditCard, path: '/mySubscription', id: 'nav-my-subscription' },
    ...(doctorChatEnabled ? [{ name: t('sidebar.patient_chat'), icon: MessageCircle, path: '/patientchat', id: 'nav-patient-chat' }] : []),
    { name: t('sidebar.support'), icon: HelpCircle, path: '/support', id: 'nav-support' },
  ];

  const patientItems = [
    { name: t('sidebar.dashboard'), icon: LayoutDashboard, path: '/patientDashboard', id: 'nav-dashboard' },
    { name: t('sidebar.appointment1'), icon: Calendar, path: '/patientAppointment', id: 'nav-patient-appointment' },
    { name: t('sidebar.myappointtment'), icon: CalendarDays, path: '/myAppointment', id: 'nav-my-appointment' },
    // When the admin enables Patient → Doctor Library, switch the single
    // "My Doctor" item for a Doctor dropdown that also exposes the library.
    ...(patientDoctorLibraryEnabled
      ? [{
          name: t('sidebar.doctor'),
          icon: Stethoscope,
          path: '/myDoctor',
          id: 'nav-doctor',
          hasDropdown: true,
          subItems: [
            { name: t('sidebar.my_doctor'), path: '/myDoctor', id: 'nav-my-doctor', activePaths: ['/myDoctor', '/doctorProfile'] },
            { name: t('sidebar.doctor_library'), path: '/doctorLibrary', id: 'nav-doctor-library' },
          ],
        }]
      : [{ name: t('sidebar.my_doctor'), icon: Stethoscope, path: '/myDoctor', id: 'nav-my-doctor' }]
    ),
    { name: t('sidebar.myprofile'), icon: UserRoundPen, path: '/patientProfile', id: 'nav-patient-profile' },
    { name: t('sidebar.payment'), icon: Landmark, path: '/payment', id: 'nav-payment' },
    // { name: t('sidebar.chat'), icon: MessageCircle, path: '/chat', id: 'nav-chat' },
    { name: t('sidebar.doctor_chat'), icon: MessageCircle, path: '/doctorchat', id: 'nav-doctor-chat' },
    { name: t('sidebar.support'), icon: HelpCircle, path: '/support', id: 'nav-support' },
  ];

  const handleNavClick = (e, targetPath) => {
    if (resnavigate) {
      e.preventDefault();
      // Remember where the user wanted to go so the dirty tab can auto-redirect
      // to it after the user confirms Discard.
      if (targetPath) {
        dispatch(setPendingNavigation({ kind: 'route', value: targetPath }));
      }
      dispatch(tryingnavigate(true));
      return;
    }
    if (window.innerWidth <= 1024) onClose();
  };

  // Fixed: Now supports all dropdown types including Reports
  const handleDropdownClick = (e, dropdownType, targetPath) => {
    if (resnavigate) {
      e.preventDefault();
      if (targetPath) {
        dispatch(setPendingNavigation({ kind: 'route', value: targetPath }));
      }
      dispatch(tryingnavigate(true));
      return;
    }

    if (window.innerWidth <= 1024) onClose();

    // Toggle based on type
    if (dropdownType === 'dashboard') {
      setDashboardDropdownOpen(prev => !prev);
    } else if (dropdownType === 'appointment') {
      setAppointmentDropdownOpen(prev => !prev);
    } else if (dropdownType === 'reports') {
      setReportsDropdownOpen(prev => !prev);
    } else if (dropdownType === 'master') {
      setMasterDropdownOpen(prev => !prev);
    } else if (dropdownType === 'patient') {
      setPatientDropdownOpen(prev => !prev);
    } else if (dropdownType === 'doctor') {
      setDoctorDropdownOpen(prev => !prev);
    } else if (dropdownType === 'subscription') {
      setSubscriptionDropdownOpen(prev => !prev);
    } else if (dropdownType === 'audit') {
      setAuditDropdownOpen(prev => !prev);
    }
  };

  // After a dirty tab confirms Discard, `resnavigate` flips back to false while
  // `pendingNavigation` still holds the original destination. Auto-perform the
  // route navigation here so the user lands where they originally clicked.
  useEffect(() => {
    if (!resnavigate && pendingNavigation?.kind === 'route' && pendingNavigation.value) {
      navigate(pendingNavigation.value, pendingNavigation.state ? { state: pendingNavigation.state } : undefined);
      dispatch(clearPendingNavigation());
      if (window.innerWidth <= 1024 && typeof onClose === 'function') {
        onClose();
      }
    }
  }, [resnavigate, pendingNavigation, navigate, dispatch, onClose]);

  // Tooltip position state
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0, visible: false, text: '' });
  const tooltipRef = useRef(null);
  const navRef = useRef(null);

  const handleTooltipShow = (e, text) => {
    if (!isOpen && window.innerWidth >= 1024) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 16,
        visible: true,
        text: text
      });
    }
  };

  const handleTooltipHide = () => {
    setTooltipPosition(prev => ({ ...prev, visible: false }));
  };

  // Update tooltip position on scroll
  useEffect(() => {
    if (!isOpen && window.innerWidth >= 1024 && tooltipPosition.visible && navRef.current) {
      const handleScroll = () => {
        setTooltipPosition(prev => ({ ...prev, visible: false }));
      };
      navRef.current.addEventListener('scroll', handleScroll);
      return () => {
        if (navRef.current) {
          navRef.current.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [tooltipPosition.visible, isOpen]);

  const renderNavItem = (item) => {
    const currentPath = location.pathname;
    const isActive = (item.activePaths ? item.activePaths.includes(currentPath) : currentPath === item.path) || item.subItems?.some(sub => sub.path === currentPath);
    const isSubItemActive = item.subItems?.some(sub => sub.path === currentPath);

    const isDashboardDropdown = item.id === 'nav-dashboard';
    const isAppointmentDropdown = item.id === 'nav-appointment';
    const isReportsDropdown = item.id === 'nav-reports';
    const isMasterDropdown = item.id === 'nav-master';
    const isPatientDropdown = item.id === 'nav-patient';
    const isDoctorDropdown = item.id === 'nav-doctor';
    const isSubscriptionDropdown = item.id === 'nav-subscription';
    const isAuditDropdown = item.id === 'nav-audit';

    const dropdownOpen =
      (isDashboardDropdown && dashboardDropdownOpen) ||
      (isAppointmentDropdown && appointmentDropdownOpen) ||
      (isReportsDropdown && reportsDropdownOpen) ||
      (isMasterDropdown && masterDropdownOpen) ||
      (isPatientDropdown && patientDropdownOpen) ||
      (isDoctorDropdown && doctorDropdownOpen) ||
      (isSubscriptionDropdown && subscriptionDropdownOpen) ||
      (isAuditDropdown && auditDropdownOpen);

    const dropdownType = isDashboardDropdown ? 'dashboard' : isAppointmentDropdown ? 'appointment' : isReportsDropdown ? 'reports' : isMasterDropdown ? 'master' : isPatientDropdown ? 'patient' : isDoctorDropdown ? 'doctor' : isSubscriptionDropdown ? 'subscription' : isAuditDropdown ? 'audit' : '';

    if (item.hasDropdown) {
      return (
        <div key={item.id} data-testid={`${item.id}-wrapper`} className="relative overflow-hidden">
          <NavLink
            to={item.path}
            data-testid={item.id}
            onClick={(e) => handleDropdownClick(e, dropdownType, item.path)}
            onMouseEnter={!isOpen && window.innerWidth >= 1024 ? (e) => handleTooltipShow(e, item.name) : undefined}
            onMouseLeave={!isOpen && window.innerWidth >= 1024 ? handleTooltipHide : undefined}
            className={`flex items-center gap-3 py-3 rounded-2xl font-medium group relative overflow-hidden w-full min-w-0
              ${(isActive || isSubItemActive) && (isOpen || window.innerWidth >= 1024)
                ? 'text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100 hover:text-[#4ABDBD]'
              } ${!isOpen ? 'justify-center px-0' : 'justify-start px-4'}`}
          >
            {/* Animated active background */}
            {(isActive || isSubItemActive) && (isOpen || window.innerWidth >= 1024) && (
              <motion.div
                layoutId="sidebar-active-bg"
                className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-2xl"
                initial={false}
                animate={{ opacity: 1 }}
                transition={{ type: 'spring', stiffness: 350, damping: 30 }}
              />
            )}

            <div className={`relative z-10 flex items-center ${!isOpen ? 'justify-center' : 'gap-3 min-w-0 flex-1'}`}>
              {(isOpen || window.innerWidth >= 1024) && (
                <item.icon
                  data-testid={`nav-link-icon-${item.id}`}
                  className={`w-5 h-5 flex-shrink-0 transition-colors duration-300 ease-in-out ${(isActive || isSubItemActive) ? 'text-white' : 'text-gray-500 group-hover:text-[#4ABDBD]'}`}
                />
              )}

              <span className={`transition-all duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 max-w-0'} overflow-hidden text-ellipsis whitespace-nowrap min-w-0 flex-1 ${(isActive || isSubItemActive) ? 'text-white font-semibold' : 'group-hover:text-[#4ABDBD]'}`}>
                {item.name}
              </span>
            </div>
          </NavLink>

          {/* Submenu */}
          {isOpen && dropdownOpen && item.subItems && (
            <div data-testid={`${item.id}-submenu`} className="ml-8 mt-2 space-y-1 overflow-hidden">
              {item.subItems.map((subItem) => {
                const isSubActive = (subItem.activePaths ? subItem.activePaths.includes(currentPath) : currentPath === subItem.path);
                return (
                  <NavLink
                    key={subItem.id}
                    to={subItem.path}
                    data-testid={subItem.id}
                    onClick={(e) => handleNavClick(e, subItem.path)}
                    className={`relative block px-4 py-2 rounded-xl text-sm overflow-hidden text-ellipsis whitespace-nowrap ${isSubActive
                      ? 'text-[#4ABDBD] font-semibold'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-[#4ABDBD]'
                      }`}
                  >
                    {isSubActive && (
                      <motion.div
                        layoutId="sidebar-sub-active-bg"
                        className="absolute inset-0 bg-[#4ABDBD]/10 rounded-xl border-l-4 border-[#4ABDBD]"
                        initial={false}
                        animate={{ opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                      />
                    )}
                    <span className="relative z-10">{subItem.name}</span>
                  </NavLink>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Regular item (no dropdown)
    const isRegularActive = (item.activePaths ? item.activePaths.includes(currentPath) : currentPath === item.path);

    return (
      <div key={item.id} data-testid={`${item.id}-wrapper`} className="relative overflow-hidden">
        <NavLink
          to={item.path}
          data-testid={item.id}
          onClick={(e) => handleNavClick(e, item.path)}
          onMouseEnter={!isOpen && window.innerWidth >= 1024 ? (e) => handleTooltipShow(e, item.name) : undefined}
          onMouseLeave={!isOpen && window.innerWidth >= 1024 ? handleTooltipHide : undefined}
          className={`flex items-center gap-3 py-3 rounded-2xl font-medium group relative overflow-hidden w-full min-w-0
            ${isRegularActive && (isOpen || window.innerWidth >= 1024)
              ? 'text-white shadow-lg'
              : 'text-gray-600 hover:bg-gray-100 hover:text-[#4ABDBD]'
            } ${!isOpen ? 'justify-center px-0' : 'justify-start px-4'}`}
        >
          {/* Animated active background */}
          {isRegularActive && (isOpen || window.innerWidth >= 1024) && (
            <motion.div
              layoutId="sidebar-active-bg"
              className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-2xl"
              initial={false}
              animate={{ opacity: 1 }}
              transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            />
          )}

          <div className={`relative z-10 flex items-center ${!isOpen ? 'justify-center' : 'gap-3 min-w-0 flex-1'}`}>
            {(isOpen || window.innerWidth >= 1024) && (
              <item.icon
                data-testid={`nav-link-icon-${item.id}`}
                className={`w-5 h-5 flex-shrink-0 transition-colors duration-300 ease-in-out ${isRegularActive ? 'text-white' : 'text-gray-500 group-hover:text-[#4ABDBD]'}`}
              />
            )}
            <span className={`transition-all duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 max-w-0'} overflow-hidden text-ellipsis whitespace-nowrap min-w-0 flex-1 ${isRegularActive ? 'text-white font-semibold' : 'group-hover:text-[#4ABDBD]'}`}>
              {item.name}
            </span>
          </div>
        </NavLink>
      </div>
    );
  };

  const navItems = userRole === 'admin' ? adminItems : userRole === 'doctor' ? doctorItems : patientItems;

  return (
    <div
      ref={ref}
      className={`fixed inset-y-0 left-0 z-50 bg-white transform transition-all duration-300 shadow-lg flex flex-col h-screen overflow-y-hidden overflow-x-hidden
        ${isOpen ? 'translate-x-0 w-64' : '-translate-x-full w-0 lg:-translate-x-0 lg:w-16 '} lg:static`}
    >
      {/* Header */}
      <div className={`py-4 flex items-center ${isOpen ? 'px-4' : 'justify-center'}`}>
        {window.innerWidth >= 1024 && (
          <div className="">
            <img src={logo} className="h-10 w-10 text-white" />
          </div>
        )}
        {isOpen && (
          <span className="ml-1.5 text-xl font-bold bg-gradient-to-r from-[#3ba5a5] to-[#3AA8A8] bg-clip-text text-transparent">
            {t('sidebar.heading1')} {t('sidebar.heading2')}
          </span>
        )}
      </div>

      {/* Profile Section */}
      {isOpen && (
        <div
          data-testid="sidebar-profile-section"
          className={`relative flex flex-col bg-gradient-to-br from-[#4ABDBD]/10 to-[#3A9B9B]/10 rounded-2xl border border-[#4ABDBD]/20 mb-4 py-3 gap-2 mt-2 px-3 flex-shrink-0 mx-4`}
        >
          {showBadge && (
            <BadgeCheck className="absolute top-2 right-2 w-6 h-6 text-white" fill="#10B981" />
          )}
          <div className="flex items-center gap-3">
            {isLoading ? (
              <HeartLoader data-testid="profile-loading" />
            ) : (user.image && user.image.trim() !== '' && !imageError) ? (
              <img
                data-testid="profile-image"
                src={getFileURL(user.image)}
                alt={user.name}

                className="w-12 h-12 rounded-full object-cover border-2 border-[#4ABDBD]/20 shadow-md"
                onError={() => setImageError(true)}
              />
            ) : (
              <div
                data-testid="profile-initial"
                className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] rounded-full shadow-lg text-white font-bold text-xl border-2 border-white"
              >
                {user.name.charAt(0).toUpperCase()}
              </div>
            )}
            {isOpen && !isLoading && (
              <div data-testid="profile-info" className="flex flex-col flex-1">
                <span data-testid="profile-name" className="font-bold text-gray-800 text-sm">
                  {user.name}
                </span>
                <span
                  data-testid="profile-role"
                  className="text-[#3A9B9B] text-xs w-fit font-medium bg-white px-2 py-1 rounded-lg shadow"
                >
                  {/* super_admin is normalised to Role='admin'; show its real label. */}
                  {isSuperAdmin ? t('sidebar.super_admin', 'Super Admin') : t(`sidebar.${userRole}s`)}
                </span>
              </div>
            )}
          </div>

          {/* Profile Completion Progress */}
          {isOpen && !isLoading && profileCompletion < 100 && userRole !== 'doctor' && userRole !== 'admin' && (
            <div
              data-testid="sidebar-profile-completion"
              className="space-y-1.5 shadow bg-white rounded-lg p-2 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleProfileNavigation('profile')}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold text-gray-700">{t('sidebar.profile_completion')}</span>
                <span className={`text-[11px] font-bold ${getPercentageTextColor(profileCompletion)}`}>{profileCompletion}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${getProgressColor(profileCompletion)} shadow-sm`}
                  style={{ width: `${profileCompletion}%` }}
                />
              </div>
            </div>
          )}

          {/* Doctor Setup Flow - Only for Doctors */}
          {isOpen && !isLoading && userRole === 'doctor' && setupStatus && !(setupStatus.point1 && setupStatus.point2 && setupStatus.point3 && setupStatus.point4) && (
            <div className="shadow bg-white rounded-lg p-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold text-gray-700">{t('sidebar.setup_progress')}</span>
                {/* {setupStatus.point1 && setupStatus.point2 && setupStatus.point3 && setupStatus.point4 && ( */}
                <span className="text-[11px] font-bold text-[#4ABDBD]">{setupStatus.point4 ? '100%' : setupStatus.point3 ? '75%' : setupStatus.point2 ? '50%' : setupStatus.point1 ? '25%' : '0%'}</span>
                {/* )} */}
              </div>

              {/* Flow Points - Hide when 100% complete */}
              {/* {!(setupStatus.point1 && setupStatus.point2 && setupStatus.point3 && setupStatus.point4) && ( */}
              <div className="relative pt-2">
                {/* Connecting Line */}
                <div className="absolute top-3.5 left-6 right-6 h-1 bg-gray-300">
                  <div
                    className={`h-full transition-all duration-500 ${setupStatus.point4 ? 'bg-[#4ABDBD]' :
                      setupStatus.point3 ? 'bg-[#4ABDBD]' :
                        setupStatus.point2 ? 'bg-[#4ABDBD]' :
                          setupStatus.point1 ? 'bg-[#4ABDBD]' : 'bg-gray-300'
                      }`}
                    style={{
                      width: setupStatus.point4 ? '100%' :
                        setupStatus.point3 ? '75%' :
                          setupStatus.point2 ? '50%' :
                            setupStatus.point1 ? '25%' : '0%'
                    }}
                  />
                </div>

                {/* Points Container */}
                <div className="relative flex items-center justify-between px-2">
                  {/* Point 1: Profile Complete */}
                  <div
                    data-testid="sidebar-setup-point-profile"
                    className="group flex flex-col items-center relative z-10 cursor-pointer gap-1"
                    onClick={() => handleProfileNavigation('profile')}
                  >
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center border-2 transition-all ${setupStatus.point1
                      ? 'bg-[#4ABDBD] border-[#4ABDBD]'
                      : 'bg-white border-gray-300'
                      }`}>
                      {setupStatus.point1 ? (
                        <Check className="w-2.5 h-2.5 text-white" />
                      ) : (
                        <span className="text-[9px] font-bold text-gray-400">P</span>
                      )}
                    </div>
                    <span className={`text-[10px] font-medium ${setupStatus.point1 ? 'text-[#4ABDBD]' : 'text-gray-400'
                      }`}>P</span>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 hidden group-hover:block">
                      <div className="bg-gray-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                        {t('sidebar.setup_point_profile')}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  </div>

                  {/* Point 2: Appointment Speciality & Type */}
                  <div
                    data-testid="sidebar-setup-point-specialities"
                    className="group flex flex-col items-center relative z-10 cursor-pointer gap-1"
                    onClick={() => handleProfileNavigation('specialities')}
                  >
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center border-2 transition-all ${setupStatus.point2
                      ? 'bg-[#4ABDBD] border-[#4ABDBD]'
                      : 'bg-white border-gray-300'
                      }`}>
                      {setupStatus.point2 ? (
                        <Check className="w-2.5 h-2.5 text-white" />
                      ) : (
                        <span className="text-[9px] font-bold text-gray-400">S</span>
                      )}
                    </div>
                    <span className={`text-[10px] font-medium ${setupStatus.point2 ? 'text-[#4ABDBD]' : 'text-gray-400'
                      }`}>S</span>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 hidden group-hover:block">
                      <div className="bg-gray-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                        {t('sidebar.setup_point_speciality')}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  </div>

                  {/* Point 3: Address */}
                  <div
                    data-testid="sidebar-setup-point-address"
                    className="group flex flex-col items-center relative z-10 cursor-pointer gap-1"
                    onClick={() => handleProfileNavigation('address')}
                  >
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center border-2 transition-all ${setupStatus.point3
                      ? 'bg-[#4ABDBD] border-[#4ABDBD]'
                      : 'bg-white border-gray-300'
                      }`}>
                      {setupStatus.point3 ? (
                        <Check className="w-2.5 h-2.5 text-white" />
                      ) : (
                        <span className="text-[9px] font-bold text-gray-400">A</span>
                      )}
                    </div>
                    <span className={`text-[10px] font-medium ${setupStatus.point3 ? 'text-[#4ABDBD]' : 'text-gray-400'
                      }`}>AD</span>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 hidden group-hover:block">
                      <div className="bg-gray-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                        {t('sidebar.setup_point_address')}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  </div>

                  {/* Point 4: Availability */}
                  <div
                    data-testid="sidebar-setup-point-availability"
                    className="group flex flex-col items-center relative z-10 cursor-pointer gap-1"
                    onClick={() => handleProfileNavigation('availability')}
                  >
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center border-2 transition-all ${setupStatus.point4
                      ? 'bg-[#4ABDBD] border-[#4ABDBD]'
                      : 'bg-white border-gray-300'
                      }`}>
                      {setupStatus.point4 ? (
                        <Check className="w-2.5 h-2.5 text-white" />
                      ) : (
                        <span className="text-[9px] font-bold text-gray-400">A</span>
                      )}
                    </div>
                    <span className={`text-[10px] font-medium ${setupStatus.point4 ? 'text-[#4ABDBD]' : 'text-gray-400'
                      }`}>AV</span>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 hidden group-hover:block">
                      <div className="bg-gray-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                        {t('sidebar.setup_point_availability')}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* )} */}
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav ref={navRef} className={`flex-1 overflow-y-auto overflow-x-hidden min-h-0 ${isOpen ? 'ml-4 pr-4 pb-2' : 'px-2'} space-y-2`} style={{ position: 'relative' }}>
        {navItems.map(renderNavItem)}
      </nav>

      {/* Footer */}
      {isOpen && (
        <footer className="p-4 border-t border-gray-200 text-center text-[9px] text-gray-500">
          <p>{t('sidebar.footer_copyright', { year: new Date().getFullYear() })}</p>
          <p className="mt-1">
            <span>{t('sidebar.app_version')}</span>{' '}
            <span className="text-[#4ABDBD] font-semibold">{t('sidebar.powered_by')}</span>
          </p>
        </footer>
      )}

      {/* Fixed Tooltip via Portal */}
      {!isOpen && window.innerWidth >= 1024 && tooltipPosition.visible && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed p-3 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white text-sm rounded-xl pointer-events-none shadow-xl whitespace-nowrap z-[99999]"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: 'translateY(-50%)',
          }}
        >
          {tooltipPosition.text}
        </div>,
        document.body
      )}
    </div>
  );
});

export default Sidebar;